'use client';

/**
 * DataTable — a typed table with sort, search, paging, a sticky header and
 * a card layout below `md`. Loading, failure and emptiness go through the
 * shared state components, so a failed load is never drawn as "no rows".
 *
 *     const columns: DataTableColumn<Matter>[] = [
 *       { key: 'name', header: 'Matter', cell: (m) => m.name, primary: true,
 *         sortable: true, sortValue: (m) => m.name },
 *       { key: 'client', header: 'Client', cell: (m) => m.client_name ?? '—' },
 *       { key: 'opened', header: 'Opened', cell: (m) => formatDate(m.opened_at),
 *         sortable: true, sortValue: (m) => m.opened_at, align: 'right' },
 *     ];
 *     <DataTable
 *       caption="Matters"
 *       columns={columns}
 *       rows={q.data?.items}
 *       rowKey={(m) => m.id}
 *       isLoading={q.isLoading}
 *       error={q.error}
 *       onRetry={() => q.refetch()}
 *       resource="matters"
 *       empty={<EmptyState title="No matters yet" />}
 *       pager={{ page, pageSize: 50, pageCount: q.data?.items.length ?? 0,
 *                total: q.data?.total, onPageChange: setPage, noun: 'matters' }}
 *     />
 *
 * Sort: pass `sort` + `onSortChange` for server-driven sort (the table only
 * reports clicks); omit them and the table sorts the rows it was given by
 * each column's `sortValue` — which sorts one page, so use it only when the
 * rows are the whole list.
 *
 * Search: pass `search={{ value, onChange }}` for server search, or
 * `filterRow={(row, q) => …}` to filter the given rows in the browser.
 *
 * Sticky header: set `maxHeight` (e.g. "70vh"); the table then scrolls inside
 * its own box and the header stays put.
 */
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { type ReactNode, useId, useMemo, useState } from 'react';

import { cn } from './cn';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Input } from './Input';
import type { LoadMoreProps, PagerProps } from './Pager';
import { LoadMore, Pager } from './Pager';
import { Skeleton } from './Skeleton';

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  key: string;
  direction: SortDirection;
}

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Header shown as the label in card layout when `header` is not plain text. */
  cardLabel?: string;
  sortable?: boolean;
  /** Client-side sort key. Required for client sort; ignored for server sort. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'left' | 'right' | 'center';
  /** Card title on phones. Exactly one column should set it. */
  primary?: boolean;
  /** Leave this column out of the phone card. */
  hideOnMobile?: boolean;
  className?: string;
}

export interface DataTableProps<T> {
  /** Accessible name of the table (rendered visually hidden). */
  caption: string;
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[] | undefined;
  rowKey: (row: T) => string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** What the rows are, lower case plural ("matters"), for state sentences. */
  resource?: string;
  /** Empty answer. Default: "No <resource> yet" (or "No results" while searching). */
  empty?: ReactNode;
  sort?: SortState | null;
  onSortChange?: (next: SortState) => void;
  defaultSort?: SortState;
  search?: { value: string; onChange: (value: string) => void; placeholder?: string; label?: string };
  filterRow?: (row: T, query: string) => boolean;
  pager?: PagerProps;
  loadMore?: LoadMoreProps;
  onRowClick?: (row: T) => void;
  maxHeight?: string;
  /** Toolbar content to the right of the search box (filters, export). */
  toolbar?: ReactNode;
  className?: string;
}

function compare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined || a === '') return 1; // blanks last
  if (b === null || b === undefined || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  isLoading = false,
  error,
  onRetry,
  resource,
  empty,
  sort: controlledSort,
  onSortChange,
  defaultSort,
  search,
  filterRow,
  pager,
  loadMore,
  onRowClick,
  maxHeight,
  toolbar,
  className,
}: DataTableProps<T>) {
  const searchId = useId();
  const serverSort = onSortChange !== undefined;
  const [localSort, setLocalSort] = useState<SortState | null>(defaultSort ?? null);
  const sort = serverSort ? (controlledSort ?? null) : localSort;
  const [localQuery, setLocalQuery] = useState('');
  const query = search ? search.value : localQuery;

  const visibleRows = useMemo(() => {
    let out = rows ? [...rows] : [];
    const q = query.trim().toLowerCase();
    if (filterRow && q) out = out.filter((r) => filterRow(r, q));
    if (!serverSort && sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col?.sortValue) {
        const get = col.sortValue;
        out.sort((a, b) => {
          const c = compare(get(a), get(b));
          return sort.direction === 'asc' ? c : -c;
        });
      }
    }
    return out;
  }, [rows, query, filterRow, serverSort, sort, columns]);

  const toggleSort = (key: string) => {
    const next: SortState =
      sort?.key === key && sort.direction === 'asc'
        ? { key, direction: 'desc' }
        : { key, direction: 'asc' };
    if (serverSort) onSortChange?.(next);
    else setLocalSort(next);
  };

  const showSearch = Boolean(search || filterRow);
  const hasRows = visibleRows.length > 0;
  const primaryCol = columns.find((c) => c.primary) ?? columns[0];

  let body: ReactNode;
  if (isLoading && !rows) {
    body = (
      <div aria-busy="true" className="space-y-2 p-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 rounded-md" />
        ))}
      </div>
    );
  } else if (error && !rows) {
    body = <ErrorState className="border-0" error={error} resource={resource} onRetry={onRetry} />;
  } else if (!hasRows) {
    body =
      query.trim() && (filterRow || search) ? (
        <EmptyState
          filtered
          body="Try a different search."
          className="m-3"
          title={resource ? `No ${resource} match` : 'No results'}
        />
      ) : (
        (empty ?? (
          <EmptyState className="m-3" title={resource ? `No ${resource} yet` : 'Nothing here yet'} />
        ))
      );
  } else {
    body = (
      <>
        {/* Table: md and up. */}
        <div
          className={cn('hidden min-w-0 overflow-x-auto md:block', maxHeight && 'overflow-y-auto')}
          style={maxHeight ? { maxHeight } : undefined}
        >
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{caption}</caption>
            <thead className="sticky top-0 z-raised bg-slate-50">
              <tr className="border-b border-slate-200">
                {columns.map((c) => {
                  const active = sort?.key === c.key;
                  const ariaSort = active
                    ? sort?.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined;
                  const sortableHere = c.sortable && (serverSort || c.sortValue);
                  const Icon = !active ? ArrowUpDown : sort?.direction === 'asc' ? ArrowUp : ArrowDown;
                  return (
                    <th
                      key={c.key}
                      aria-sort={ariaSort}
                      className={cn(
                        'px-3 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted',
                        ALIGN[c.align ?? 'left'],
                        c.className,
                      )}
                      scope="col"
                    >
                      {sortableHere ? (
                        <button
                          className="inline-flex items-center gap-1 rounded hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                          type="button"
                          onClick={() => toggleSort(c.key)}
                        >
                          {c.header}
                          <Icon aria-hidden className={active ? 'text-fg' : 'opacity-50'} size={12} />
                        </button>
                      ) : (
                        c.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={cn(
                    'border-b border-slate-100 last:border-0',
                    onRowClick && 'cursor-pointer hover:bg-slate-50',
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn('px-3 py-2.5 align-top text-fg', ALIGN[c.align ?? 'left'], c.className)}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Cards: below md. */}
        <ul aria-label={caption} className="divide-y divide-slate-100 md:hidden">
          {visibleRows.map((row) => (
            <li
              key={rowKey(row)}
              className={cn('space-y-1.5 px-3 py-3', onRowClick && 'cursor-pointer active:bg-slate-50')}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {primaryCol ? <div className="font-medium text-fg">{primaryCol.cell(row)}</div> : null}
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                {columns
                  .filter((c) => c !== primaryCol && !c.hideOnMobile)
                  .map((c) => (
                    <div key={c.key} className="contents">
                      <dt className="text-fg-muted">
                        {c.cardLabel ?? (typeof c.header === 'string' ? c.header : c.key)}
                      </dt>
                      <dd className="min-w-0 text-fg">{c.cell(row)}</dd>
                    </div>
                  ))}
              </dl>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <div className={cn('min-w-0 space-y-2', className)}>
      {showSearch || toolbar ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {showSearch ? (
            <div className="w-full sm:max-w-xs">
              <label className="sr-only" htmlFor={searchId}>
                {search?.label ?? `Search ${resource ?? caption.toLowerCase()}`}
              </label>
              <Input
                autoComplete="off"
                id={searchId}
                placeholder={search?.placeholder ?? 'Search…'}
                prefix={<Search aria-hidden size={13} strokeWidth={1.75} />}
                type="search"
                value={query}
                onChange={(e) => (search ? search.onChange(e.target.value) : setLocalQuery(e.target.value))}
              />
            </div>
          ) : (
            <span />
          )}
          {toolbar ? <div className="flex flex-wrap items-center gap-2">{toolbar}</div> : null}
        </div>
      ) : null}
      {error && rows ? (
        <ErrorState compact error={error} resource={resource} onRetry={onRetry} />
      ) : null}
      <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white">{body}</div>
      {pager ? <Pager {...pager} /> : null}
      {loadMore ? <LoadMore {...loadMore} /> : null}
    </div>
  );
}
